import { CheckoutFieldType } from "./types";

export const CHECKOUT_FIELD_TYPES: Array<{ value: CheckoutFieldType; label: string; needsOptions: boolean }> = [
  { value: "text", label: "Texto curto", needsOptions: false },
  { value: "textarea", label: "Texto longo", needsOptions: false },
  { value: "select", label: "Lista suspensa", needsOptions: true },
  { value: "radio", label: "Múltipla escolha (única)", needsOptions: true },
  { value: "checkbox", label: "Múltiplas seleções", needsOptions: true },
  { value: "date", label: "Data", needsOptions: false },
];

export function getCheckoutFieldTypeLabel(fieldType: CheckoutFieldType) {
  return CHECKOUT_FIELD_TYPES.find((item) => item.value === fieldType)?.label ?? fieldType;
}

export function fieldTypeNeedsOptions(fieldType: CheckoutFieldType) {
  return CHECKOUT_FIELD_TYPES.find((item) => item.value === fieldType)?.needsOptions ?? false;
}

export function normalizeQuestionOptions(rawValue: any) {
  if (Array.isArray(rawValue)) {
    return rawValue.map((value: string) => String(value).trim()).filter(Boolean);
  }

  if (typeof rawValue === "object" && rawValue !== null) {
    return []; // JSON options objects don't normalize to text dropdown arrays
  }

  if (!rawValue) return [];

  return String(rawValue)
    .split(/\r?\n|,/)
    .map((value) => value.trim())
    .filter(Boolean);
}

export function isQuestionSelectedForTier(question: { tier_ids?: string[] | null }, tierId: string) {
  return !question.tier_ids?.length || question.tier_ids.includes(tierId);
}

export function isQuestionAvailableForTiers(question: { tier_ids?: string[] | null; options?: any; field_type?: string }, tierIds: string[]) {
  // Always respect tier_ids regardless of validation — tier filtering controls visibility,
  // and answer validation (validateQuestionOptions) enforces code correctness.
  if (!question.tier_ids?.length) return true;
  return question.tier_ids.some((tierId) => tierIds.includes(tierId));
}

export function serializeQuestionAnswer(fieldType: CheckoutFieldType, value: string | string[]) {
  if (fieldType === "checkbox") {
    return JSON.stringify(Array.isArray(value) ? value : normalizeQuestionOptions(value));
  }

  return Array.isArray(value) ? value.join(", ") : value;
}

export function parseQuestionAnswer(fieldType: CheckoutFieldType, value: string | null | undefined) {
  if (!value) return fieldType === "checkbox" ? [] : "";

  if (fieldType === "checkbox") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) {
        return parsed.map((item) => String(item));
      }
    } catch {
      return value
        .split(/\r?\n|,/)
        .map((item) => item.trim())
        .filter(Boolean);
    }

    return [];
  }

  return value;
}

export function hasQuestionAnswer(fieldType: CheckoutFieldType, value: string | null | undefined) {
  if (!value) return false;

  if (fieldType === "checkbox") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed.length > 0 : false;
    } catch {
      return value
        .split(/\r?\n|,/)
        .map((item) => item.trim())
        .filter(Boolean)
        .length > 0;
    }
  }

  return value.trim().length > 0;
}

export function validateQuestionOptions(question: any, value: string | null | undefined): string | null {
  let opts = question.options;
  if (typeof opts === "string") {
    try { opts = JSON.parse(opts); } catch {}
  }

  const isValidationEnabled = opts?.isValidationEnabled === true || opts?.is_validation_enabled === true;

  if (question.field_type === "text" && isValidationEnabled) {
    if (!value || String(value).trim().length === 0) {
      return "Um código é obrigatório para este campo.";
    }

    const rawCodes: string[] = Array.isArray(opts?.codes) ? opts.codes : [];
    if (rawCodes.length === 0) {
      return "Nenhum código de validação configurado para este campo.";
    }

    // Clean provided input (strip invisible spaces/BOM & trim)
    const cleanProvided = String(value)
      .replace(/[\u200B-\u200D\uFEFF]/g, "")
      .trim()
      .toLowerCase();

    // Standard allowed codes set
    const allowedCodesSet = new Set(
      rawCodes.map((c: string) =>
        String(c).replace(/[\u200B-\u200D\uFEFF]/g, "").trim().toLowerCase()
      ).filter(Boolean)
    );

    // Allowed codes digits-only set
    const allowedDigitsSet = new Set(
      Array.from(allowedCodesSet).map((c) => c.replace(/\D/g, "")).filter(Boolean)
    );

    // Check single exact or single digits match
    const singleDigits = cleanProvided.replace(/\D/g, "");
    if (allowedCodesSet.has(cleanProvided) || (singleDigits && allowedDigitsSet.has(singleDigits))) {
      return null;
    }

    // Check multiple codes separated by commas, slashes, semicolons, 'e', 'and', or whitespace
    // (e.g. when buying multiple tickets or entering multiple IDs like "4558128, 8182629" or "4558128 / 8182629")
    const parts = cleanProvided
      .split(/[,;\/\n]|\s+e\s+|\s+and\s+|\s+/)
      .map((p) => p.trim())
      .filter(Boolean);

    if (parts.length > 1) {
      const allValid = parts.every((part) => {
        const partDigits = part.replace(/\D/g, "");
        return allowedCodesSet.has(part) || (partDigits && allowedDigitsSet.has(partDigits));
      });

      if (allValid) {
        return null;
      }
    }

    return "Código inválido ou não autorizado.";
  }

  return null;
}
